import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { focusInputOnCellFocused, tabToNextCellByColumn } from 'components/AgGrid/utils';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import type { ConstructionTypeData, ContractStatusEnum, WorkOrderPriceItem } from 'gql/graphql';
import { ProjectMethodologyEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { OtherViewModal } from 'pages/Project/Common/Contract/OtherView';
import { MatchMtrDialog } from 'pages/Project/UerProject/MatchMtrDialog';
import { EditMtrDialog } from 'pages/Project/UerProject/PriceList/EditMtrDialog';
import { CreateOrEditRatio } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer';
import { getCheckedIdsArr } from 'utils';

import type { GetRowIdParams } from 'ag-grid-community/dist/types/core/interfaces/iCallbackParams';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AddActKC2 } from '../AddActKC2';
import { AddCoefficient } from '../AddCoefficient';
import { AddCoefficientToActKC2 } from '../AddCoefficientToActKC2';
import { AddMtrDialog } from '../AddMtrDialog';
import { EditOtherDistribution } from '../EditOtherDistribution';
import { EditRationaleDialog } from '../EditRationaleDialog';
import { useSeasons } from '../hooks/useSeasons';

import { getUerWorkOrderRowClass, getWorkOrderTableKey } from './helpers';
import { useWorkOrderActs, useWorkOrderColumns, useWorkOrderData } from './hooks';
import { useDeleteFromWorkOrder } from './hooks/useDeleteFromWorkOrder';
import { useWorkOrderRowDataRefresh } from './hooks/useWorkOrderRowDataRefresh';
import { WorkOrderMenuActionTypes } from './types';

interface IWorkOrderTableProps {
    workOrderId: number;
    objectId: number | undefined;
    subObjectId: number | undefined;
    constructionTypeId: number;
    constructionTypes: ConstructionTypeData[];
    workOrderHasInProgress: boolean;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    costRounding: number;
    contractStatus: ContractStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    reloadWorkOrderTable: () => void;
    skzsPanelRefresh: () => void;
    onActsKc2AnalyticsOpen: (newActsKc2AnalyticsRootObjectId?: number) => void;
    refreshProjectData?: () => void;
    catalogVersion?: string;
    catalogName?: string;
}

const getRowId = (params: GetRowIdParams<WorkOrderPriceItem>) => String(params.data.clientId);

export const WorkOrderTable = forwardRef((props: IWorkOrderTableProps, ref: any) => {
    const {
        workOrderId,
        objectId,
        subObjectId,
        costRounding,
        constructionTypeId,
        constructionTypes,
        workOrderStatus,
        workOrderHasInProgress,
        contractStatus,
        isDisabledByProjectStatus,
        reloadWorkOrderTable,
        skzsPanelRefresh,
        onActsKc2AnalyticsOpen,
        refreshProjectData,
        catalogVersion,
        catalogName,
    } = props;
    const { id: projectId } = useParams();
    const navigate = useNavigate();

    const [openedModal, setOpenedModal] = useState<{
        type: WorkOrderMenuActionTypes;
        rowData: WorkOrderPriceItem;
        options?: any;
    } | null>(null);

    const openModalHandler = useCallback(
        (type: WorkOrderMenuActionTypes, rowData: WorkOrderPriceItem, options?: any) => {
            if (type === WorkOrderMenuActionTypes.actsKc2Analytics) {
                onActsKc2AnalyticsOpen(rowData.rootObjectId);

                return;
            }

            setOpenedModal({ type, rowData, options });
        },
        [onActsKc2AnalyticsOpen],
    );

    const changeModalOptions = useCallback((options: any) => {
        setOpenedModal((prev) => (prev ? { ...prev, options } : null));
    }, []);

    const closeModals = useCallback(() => {
        setOpenedModal(null);
    }, []);

    const { projectData, refreshProject } = useProjectData(projectId, false);
    const getContractData = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData,
        [projectData?.contractNavigation?.data?.contractNavData],
    );
    const hasWorkKind = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind],
    );
    const tableKey = useMemo(
        () =>
            getWorkOrderTableKey({
                workOrderId,
                constructionTypeId,
                objectId,
                subObjectId,
            }),
        [constructionTypeId, objectId, subObjectId, workOrderId],
    );

    const [seasons] = useSeasons(workOrderId, true);
    const { acts, refreshActs } = useWorkOrderActs({
        workOrderId,
        constructionTypeId,
    });

    const { workOrderPriceData, fetchedItems, totalItems, changeFilterHandler } = useWorkOrderData({
        workOrderId,
        constructionTypeId,
        objectId,
        subObjectId,
    });

    const { columns, isColumnVisible, fullSetSettingsColumns, tableSettingsConfirm, checkedRows, resetCheckedRows } =
        useWorkOrderColumns({
            workOrderId,
            workOrderHasInProgress,
            objectId,
            subObjectId,
            workOrderStatus,
            costRounding,
            seasons,
            contractStatus,
            isDisabledByProjectStatus,
            refreshPriceListData: reloadWorkOrderTable,
            workOrderActs: acts,
            refreshActs,
            constructionTypeId,
            changeFilterHandler,
            openModalHandler,
            skzsPanelRefresh,
            hasWorkKind,
            refreshProjectData,
            useAgCellRowDataRefresh: useWorkOrderRowDataRefresh,
            useAgCellRowDataRefreshParams: {
                workOrderId,
                constructionTypeId,
            },
        });

    const tableRowClassGetter = useMemo(() => getUerWorkOrderRowClass(workOrderStatus), [workOrderStatus]);
    const isTableRdy = useMemo(() => !!(workOrderId && constructionTypeId), [constructionTypeId, workOrderId]);
    const checkedRowsArr = useMemo(() => getCheckedIdsArr(checkedRows), [checkedRows]);

    const { deleteStatus, handleDeleteFromWorkOrder, deleteErr, deleteSetData, deleteData } = useDeleteFromWorkOrder({
        workOrderId,
        checkedRows,
        singleDeleteRow: openedModal?.rowData,
    });

    const onSuccessDelete = useCallback(() => {
        resetCheckedRows();
        reloadWorkOrderTable();
    }, [reloadWorkOrderTable, resetCheckedRows]);

    const updateActsWithScroll = useCallback(
        (actId: number | undefined) => {
            refreshActs();
            reloadWorkOrderTable();
            refreshProjectData?.();
            setTimeout(() => {
                navigate('./', {
                    state: {
                        actNavInfo: {
                            actId,
                            constructionTypeId,
                        },
                    },
                });
            }, 1000);
        },
        [constructionTypeId, navigate, refreshActs, refreshProjectData, reloadWorkOrderTable],
    );

    return (
        <>
            <TableTopPanel
                fetchedItems={fetchedItems}
                totalItems={totalItems}
                isColumnVisible={isColumnVisible}
                optionalColumns={fullSetSettingsColumns}
                handleTableSettingsConfirm={tableSettingsConfirm}
                showPinColumn
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                tableApi={ref?.current}
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteErr}
                    deleteSetData={deleteSetData}
                    onSuccess={onSuccessDelete}
                    handleDelete={handleDeleteFromWorkOrder}
                />
            </TableTopPanel>
            <div className="ag-theme-alpine workOrderPrice">
                {isTableRdy && (
                    <AgGridTable
                        ref={ref}
                        datasource={workOrderPriceData}
                        columnDefs={columns}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        rowClassRules={tableRowClassGetter}
                        context={{
                            tableUniqueId: tableKey,
                        }}
                        defaultColDef={{
                            sortable: false,
                        }}
                        getRowId={getRowId}
                        key={tableKey}
                        tabToNextCell={tabToNextCellByColumn}
                        onCellFocused={focusInputOnCellFocused}
                        className={tableKey}
                    />
                )}
            </div>
            {openedModal?.type === WorkOrderMenuActionTypes.addKC2 && openedModal?.rowData?.rootObjectId && (
                <AddActKC2
                    gridTableRef={ref?.current}
                    contractor={getContractData?.metadata?.contractor}
                    contractorAddress={getContractData?.metadata?.contractorAddress}
                    workOrderId={workOrderId}
                    constructionTypeId={constructionTypeId}
                    objectId={openedModal?.rowData?.rootObjectId}
                    costGroup={openedModal?.options?.costGroup}
                    statusWorkOrder={workOrderStatus}
                    onAdd={updateActsWithScroll}
                    statusContract={getContractData?.status}
                    isDisabled={isDisabledByProjectStatus}
                    isOpenedOuter={openedModal?.type === WorkOrderMenuActionTypes.addKC2}
                    closeOuterHandler={closeModals}
                    withBtn={false}
                />
            )}
            {openedModal?.type === WorkOrderMenuActionTypes.editUer && (
                <CreateOrEditUer
                    chapter={openedModal?.rowData?.chapter}
                    onSave={reloadWorkOrderTable}
                    closeModal={closeModals}
                    constructionTypeId={constructionTypeId}
                    contractId={projectData?.contractId}
                    workOrderStatus={workOrderStatus}
                    uerId={openedModal?.rowData?.entryId}
                    workOrderPriceId={openedModal?.rowData?.itemId}
                    noShowButton
                    fromWorkOrder
                    catalogVersion={catalogVersion}
                    catalogName={catalogName}
                />
            )}
            {openedModal?.type === WorkOrderMenuActionTypes.addMtr && (
                <AddMtrDialog
                    workOrderStatus={workOrderStatus}
                    shouldAddToActKC2={workOrderStatus === WorkOrderStatusEnum.InProgress}
                    mtrId={openedModal?.rowData?.itemId}
                    chapter={openedModal?.rowData?.chapter}
                    onSuccess={() => {
                        closeModals();
                        reloadWorkOrderTable();
                    }}
                    closeModal={closeModals}
                />
            )}
            {openedModal?.type === WorkOrderMenuActionTypes.addCoefficient &&
                workOrderStatus === WorkOrderStatusEnum.InProgress &&
                !openedModal?.options?.strategy && (
                    <AddCoefficientToActKC2
                        actKC2List={acts ?? []}
                        openModalStrategy={changeModalOptions}
                        closeModal={closeModals}
                    />
                )}
            {openedModal?.type === WorkOrderMenuActionTypes.addCoefficient &&
                !openedModal?.options?.strategy &&
                workOrderStatus === WorkOrderStatusEnum.OnFormation && (
                    <AddCoefficient
                        workOrderStatus={workOrderStatus}
                        uerId={openedModal?.rowData?.itemId}
                        closeModal={closeModals}
                        onSuccess={reloadWorkOrderTable}
                        uer={openedModal?.rowData}
                        contractId={projectData?.contractId}
                    />
                )}
            {openedModal?.type === WorkOrderMenuActionTypes.addCoefficient && openedModal?.options?.strategy && (
                <AddCoefficient
                    workOrderStatus={workOrderStatus}
                    uerId={openedModal?.rowData?.itemId}
                    actKC2Id={openedModal?.options?.actId}
                    addStrategy={openedModal?.options?.strategy}
                    closeModal={closeModals}
                    onSuccess={reloadWorkOrderTable}
                    uer={openedModal?.rowData}
                    contractId={projectData?.contractId}
                />
            )}
            {openedModal?.type === WorkOrderMenuActionTypes.editNote && (
                <EditRationaleDialog
                    refreshData={reloadWorkOrderTable}
                    onClose={closeModals}
                    itemId={openedModal?.rowData?.itemId}
                    itemCode={openedModal?.rowData?.code}
                    itemName={openedModal?.rowData?.name}
                    itemNote={openedModal?.rowData?.rationale}
                    itemType={openedModal?.rowData?.type}
                />
            )}

            {openedModal?.type === WorkOrderMenuActionTypes.editCoefficient && (
                <CreateOrEditRatio
                    onClose={closeModals}
                    onConfirm={() => {
                        closeModals();
                        reloadWorkOrderTable();
                    }}
                    workOrderStatus={workOrderStatus}
                    uer={openedModal?.rowData}
                    coeffId={openedModal?.rowData?.entryId}
                />
            )}

            {openedModal?.type === WorkOrderMenuActionTypes.matchMtr && (
                <MatchMtrDialog
                    contractId={projectData?.contractId}
                    onMatchApply={closeModals}
                    refreshProject={() => {
                        refreshProject();
                        reloadWorkOrderTable();
                    }}
                    chapter={openedModal?.rowData?.chapter}
                    workOrderStatus={workOrderStatus}
                    isChange={false}
                    priceListId={openedModal?.rowData?.entryId}
                    workOrderPriceId={openedModal?.rowData?.itemId}
                />
            )}

            {openedModal?.type === WorkOrderMenuActionTypes.editMtr && (
                <EditMtrDialog
                    onCloseMatchDialog={closeModals}
                    setIsOpenModal={closeModals}
                    updateMtr={reloadWorkOrderTable}
                    chapter={openedModal?.rowData?.chapter}
                    workOrderStatus={workOrderStatus}
                    workOrderPriceId={openedModal?.rowData?.itemId}
                    mtrId={openedModal?.rowData?.entryId}
                />
            )}

            {openedModal?.type === WorkOrderMenuActionTypes.editOtherDistribution && (
                <EditOtherDistribution
                    onClose={() => {
                        closeModals();
                        reloadWorkOrderTable();
                    }}
                    workOrderId={workOrderId}
                    currentConstructionTypeId={constructionTypeId}
                    costRounding={projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0}
                    workOrderStatus={workOrderStatus}
                    otherId={openedModal?.rowData?.itemId}
                    defaultObjectId={openedModal?.rowData?.rootObjectId}
                />
            )}

            {openedModal?.type === WorkOrderMenuActionTypes.viewOther && (
                <OtherViewModal
                    costRounding={projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0}
                    constructionTypes={constructionTypes}
                    contractId={projectData?.contractId}
                    methodology={ProjectMethodologyEnum.Uer}
                    onClose={closeModals}
                    contractOtherCostId={openedModal?.rowData?.entryId}
                />
            )}

            <DeleteItemFrom
                isOpened={Boolean(openedModal?.type === WorkOrderMenuActionTypes.singleDelete && openedModal?.rowData)}
                closeDeleteModal={closeModals}
                deleteData={deleteData}
                deleteStatus={deleteStatus}
                deleteError={deleteErr}
                deleteSetData={deleteSetData}
                handleDelete={handleDeleteFromWorkOrder}
                handleSuccess={onSuccessDelete}
            />
        </>
    );
});

WorkOrderTable.displayName = 'WorkOrderTable';
